import type { JSX } from 'react';
import { BrowserRouter as Router, Route, Routes } from 'react-router-dom';

import PrivateRoute from './PrivateRoute';
import { ViewProvider } from '../components/layout/ViewContext'; // Import ViewProvider
import AuditLogList from '../pages/admin/auditLogs/List';
import BulkEmailCreate from '../pages/admin/bulkEmail/Create';
import BulkEmailEdit from '../pages/admin/bulkEmail/Edit';
import BulkEmailList from '../pages/admin/bulkEmail/List';
import CompanyInfoCreate from '../pages/admin/companyInfo/Create';
import CompanyInfoEdit from '../pages/admin/companyInfo/Edit';
import CompanyInfoList from '../pages/admin/companyInfo/List';
import CountryCreate from '../pages/admin/country/Create';
import CountryEdit from '../pages/admin/country/Edit';
import CountryList from '../pages/admin/country/List';
import CourseCreate from '../pages/admin/course/Create';
import CourseEdit from '../pages/admin/course/Edit';
import CourseList from '../pages/admin/course/List';
import CourseRequestList from '../pages/admin/courseRequest/List';
import CourseRequestReview from '../pages/admin/courseRequest/Review';
import Dashboard from '../pages/admin/Dashboard';
import InvoiceList from '../pages/admin/invoice/List';
import MemberEdit from '../pages/admin/member/Edit';
import MemberList from '../pages/admin/member/List';
import MemberTypeCreate from '../pages/admin/memberType/Create';
import MemberTypeEdit from '../pages/admin/memberType/Edit';
import MemberTypeList from '../pages/admin/memberType/List';
import PinCreate from '../pages/admin/pin/Create';
import PinEdit from '../pages/admin/pin/Edit';
import PinList from '../pages/admin/pin/List';
import UserCreate from '../pages/admin/users/Create';
import UserEdit from '../pages/admin/users/Edit';
import UsersList from '../pages/admin/users/List';
import MemberBucketList from '../pages/member/BucketList';
import MemberCoursesPlayed from '../pages/member/CoursesPlayed';
import MemberDashboard from '../pages/member/Dashboard';
import MemberHomeCourses from '../pages/member/HomeCourses';
import MemberProfile from '../pages/member/Profile';
import MemberProfileGolf from '../pages/member/ProfileGolf';
import MemberProfileSettings from '../pages/member/ProfileSettings';
import CourseProfile from '../pages/public/CourseProfile';
import Explore from '../pages/public/Explore';
import ForgotPassword from '../pages/public/ForgotPassword';
import Login from '../pages/public/Login';
import Pay from '../pages/public/Pay';
import PaymentRequired from '../pages/public/PaymentRequired';
import ResetPassword from '../pages/public/ResetPassword';
import SharedCoursesPlayed from '../pages/public/SharedCoursesPlayed';
import SharedProfile from '../pages/public/SharedProfile';
import Unsubscribe from '../pages/public/Unsubscribe';

// Import route here

const AppRouter = (): JSX.Element => (
  <ViewProvider>
    {' '}
    {/* Wrap all routes in the ViewProvider */}
    <Router>
      <Routes>
        <Route path="/" element={<Explore />} />
        <Route path="/login" element={<Login />} />
        <Route path="/explore" element={<Explore />} />
        {/* Public: a course's read-only page. Linked from Courses Played, and later from a shared list. */}
        <Route path="/courses/:id" element={<CourseProfile />} />
        {/* Public: a member's shared Courses Played list. The segment is their share code. */}
        <Route
          path="/shared/courses-played/:shareId"
          element={<SharedCoursesPlayed />}
        />
        {/* Public: a member's shared profile. The segment is their profile share code. */}
        <Route path="/shared/profile/:shareId" element={<SharedProfile />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/unsubscribe" element={<Unsubscribe />} />
        <Route path="/pay/:invoiceId" element={<Pay />} />
        <Route
          path="/payment-required"
          element={
            <PrivateRoute>
              <PaymentRequired />
            </PrivateRoute>
          }
        />

        {/* Protected member routes */}
        <Route
          path="/dashboard"
          element={
            <PrivateRoute>
              <MemberDashboard />
            </PrivateRoute>
          }
        />
        <Route
          path="/member/profile"
          element={
            <PrivateRoute>
              <MemberProfile />
            </PrivateRoute>
          }
        />
        <Route
          path="/member/profile/settings"
          element={
            <PrivateRoute>
              <MemberProfileSettings />
            </PrivateRoute>
          }
        />
        <Route
          path="/member/profile/golf"
          element={
            <PrivateRoute>
              <MemberProfileGolf />
            </PrivateRoute>
          }
        />
        <Route
          path="/member/home-courses"
          element={
            <PrivateRoute>
              <MemberHomeCourses />
            </PrivateRoute>
          }
        />
        <Route
          path="/member/courses-played"
          element={
            <PrivateRoute>
              <MemberCoursesPlayed />
            </PrivateRoute>
          }
        />
        <Route
          path="/member/bucket-list"
          element={
            <PrivateRoute>
              <MemberBucketList />
            </PrivateRoute>
          }
        />

        {/* Protected admin routes */}
        <Route
          path="/admin/dashboard"
          element={
            <PrivateRoute>
              <Dashboard />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/auditlogs"
          element={
            <PrivateRoute>
              <AuditLogList />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/companyinfo"
          element={
            <PrivateRoute>
              <CompanyInfoList />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/companyinfo/create"
          element={
            <PrivateRoute>
              <CompanyInfoCreate />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/companyinfo/:id"
          element={
            <PrivateRoute>
              <CompanyInfoEdit />
            </PrivateRoute>
          }
        />

        <Route
          path="/admin/bulkemails"
          element={
            <PrivateRoute>
              <BulkEmailList />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/bulkemails/create"
          element={
            <PrivateRoute>
              <BulkEmailCreate />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/bulkemails/:id"
          element={
            <PrivateRoute>
              <BulkEmailEdit />
            </PrivateRoute>
          }
        />

        <Route
          path="/admin/users"
          element={
            <PrivateRoute>
              <UsersList />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/users/create"
          element={
            <PrivateRoute>
              <UserCreate />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/users/:id"
          element={
            <PrivateRoute>
              <UserEdit />
            </PrivateRoute>
          }
        />

        <Route
          path="/admin/members"
          element={
            <PrivateRoute>
              <MemberList />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/members/:id"
          element={
            <PrivateRoute>
              <MemberEdit />
            </PrivateRoute>
          }
        />

        <Route
          path="/admin/countries"
          element={
            <PrivateRoute>
              <CountryList />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/countries/create"
          element={
            <PrivateRoute>
              <CountryCreate />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/countries/:id"
          element={
            <PrivateRoute>
              <CountryEdit />
            </PrivateRoute>
          }
        />

        <Route
          path="/admin/pins"
          element={
            <PrivateRoute>
              <PinList />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/pins/create"
          element={
            <PrivateRoute>
              <PinCreate />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/pins/:id"
          element={
            <PrivateRoute>
              <PinEdit />
            </PrivateRoute>
          }
        />

        <Route
          path="/admin/courses"
          element={
            <PrivateRoute>
              <CourseList />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/courses/create"
          element={
            <PrivateRoute>
              <CourseCreate />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/courses/:id"
          element={
            <PrivateRoute>
              <CourseEdit />
            </PrivateRoute>
          }
        />

        <Route
          path="/admin/course-requests"
          element={
            <PrivateRoute>
              <CourseRequestList />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/course-requests/:id"
          element={
            <PrivateRoute>
              <CourseRequestReview />
            </PrivateRoute>
          }
        />

        <Route
          path="/admin/membertypes"
          element={
            <PrivateRoute>
              <MemberTypeList />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/membertypes/create"
          element={
            <PrivateRoute>
              <MemberTypeCreate />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/membertypes/:id"
          element={
            <PrivateRoute>
              <MemberTypeEdit />
            </PrivateRoute>
          }
        />

        <Route
          path="/admin/invoices"
          element={
            <PrivateRoute>
              <InvoiceList />
            </PrivateRoute>
          }
        />
      </Routes>
    </Router>
  </ViewProvider>
);

export default AppRouter;
